import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Container from "../../components/Container";
import WorkContentEditTable from "../../components/work-contents/WorkContentEditTable";
import OverlayLoading from "../../components/OverlayLoading";
import WorkHeader from "../../components/work-contents/WorkHeader";
import WorkContentTableHeader from "../../components/work-contents/WorkContentTableHeader";
import Dialog from "../../components/Dialog";
import CancelButton from "../../components/forms/buttons/CancelButton";
import GreenButton from "../../components/forms/buttons/GreenButton";
import PurpleButton from "../../components/forms/buttons/PurpleButton";
import OutlineButton from "../../components/forms/buttons/OutlineButton";
import { workContentService } from "../../services/WorkContentService";
import { dateService } from "../../services/DateService";
import { useSnackbarContext } from "../../components/snackbar/SnackbarContext";

const ConfirmIcon = () => (
  <div className="confirm-icon" aria-hidden="true" id="confirmIcon">
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 9v4M12 17h.01"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <path
        d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);
/**
 * 作業内容編集ページ
 * @class WorkContentsEditPage
 */
export default function WorkContentsEditPage() {
  const [workContents, setWorkContents] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [isContinueOpen, setContinueOpen] = useState(false);
  const [isBackOpen, setBackOpen] = useState(false);
  const { workId } = useParams();
  const { showSnackbar } = useSnackbarContext();
  const navigate = useNavigate();

  /**
   * 対象の作業内容詳細を取得する
   * ページ上部にスクロールする（アニメーションなし）
   * @memberof WorkContentsEditPage
   */
  useEffect(() => {
    const getWorkContent = async () => {
      try {
        const result = (await workContentService.get(workId)).data;
        if (result.success) {
          setWorkContents(result.body);
        }
        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    };
    getWorkContent();

    // ページ上部にスクロールする（アニメーションなし）
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [workId]);
  /**
   * 変更された入力欄の値を作業内容に反映する
   * @param {string} name 項目名
   * @param {string} value 値
   * @memberof WorkContentsEditPage
   */
  const onChange = (name, value) => {
    try {
      const params = { ...workContents, [name]: value };
      setWorkContents(params);
    } catch (error) {
      console.error(error);
    }
  };
  const onSave = async () => {
    const result = (await workContentService.updateDirect(workId, workContents))
      .data;
    if (!result.success) {
      showSnackbar("error", result.message);
    }
  };
  /**
   * 入力データをチェックする
   * @memberof WorkContentsEditPage
   */
  const checkParams = () => {
    let errors = {};
    if (!workContents?.endVolume) {
      errors = { ...errors, endVolume: "この項目は必須項目です" };
    } else if (
      Number(workContents?.startVolume) > Number(workContents?.endVolume)
    ) {
      errors = {
        ...errors,
        endVolume: "終了時出来高は開始時出来高以上で入力してください",
      };
    }
    setErrors(errors);
    console.log(errors);
    setContinueOpen(Object.keys(errors).length > 0);
  };
  /**
   * 作業終了日時を保存する
   * @memberof WorkContentsEditPage
   */
  const onFinish = () => {
    setContinueOpen(false);
    onChange("endedAt", dateService.formatCurrentDatetime());
  };
  return (
    <Container title="作業内容入力">
      <div id="work-contents-edit-page">
        <WorkHeader
          title="作業内容入力"
          subTitle="WORK DETAIL"
          workId={workId}
          onBack={() => setBackOpen(true)}
        />
        <WorkContentTableHeader
          workType={workContents.workType}
          workPlace={workContents?.workplace}
          modelType={workContents?.modelTypeLabel}
          workProcess={workContents?.workProcessLabel}
          modelNumber={workContents?.modelNumberLabel}
        />
        <WorkContentEditTable
          workContents={workContents}
          onChange={onChange}
          onSave={onSave}
          onFinish={checkParams}
          errors={errors}
        />
      </div>
      <div
        className="mobile-fixed-actions"
        aria-label="スマートフォン用操作ボタン"
      >
        <OutlineButton title="保存" onClick={onSave} />
        <GreenButton title="作業を完了" onClick={checkParams} />
      </div>

      <OverlayLoading open={isLoading} />

      <Dialog
        open={isContinueOpen}
        onClose={() => isContinueOpen(false)}
        title="未入力の項目があります"
        icon={<ConfirmIcon />}
        actions={
          <>
            <CancelButton onClick={() => setContinueOpen(false)} />
            <GreenButton title="このまま完了" onClick={onFinish} />
          </>
        }
      >
        終了時出来高が未入力、または開始時出来高より小さい可能性があります。このまま作業完了処理へ進みますか？
      </Dialog>
      <Dialog
        open={isBackOpen}
        onClose={() => setBackOpen(false)}
        title="保存せずに戻りますか？"
        icon={<ConfirmIcon />}
        actions={
          <>
            <CancelButton onClick={() => setBackOpen(false)} />
            <PurpleButton title="保存せず戻る" onClick={() => navigate(-1)} />
          </>
        }
      >
        入力内容が保存されていません。一覧画面へ戻ると変更内容が失われる可能性があります。
      </Dialog>
    </Container>
  );
}
